import { useIntl } from 'react-intl';

import { WikusAvatar } from '@wikus-ui/components/core/avatar';
import { WikusButton } from '@wikus-ui/components/core/button';
import { WikusInfiniteScroll } from '@wikus-ui/components/complex/infinite-scroll';
import { WikusItem, WikusSection } from '@wikus-ui/components/core/spacing';
import { WikusModelCard } from '@wikus-ui/components/complex/model-card';
import { WikusPanel } from '@wikus-ui/components/core/card';

import * as CustomerActions from '../../actions/customer';

import { Customer } from 'src/model';
import { RootState } from 'src/reducers';
import { getInitials } from '@wikus-ui/utils';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { usePaginate } from '@wikus-ui/hooks/usePaginate';
import { useSelector } from 'react-redux';

import { CustomerFilter } from '../../components/customer/users/filter';
import { WikusFilterValue } from '@wikus-ui/components/complex/filter';
import { WikusHr } from '@wikus-ui/components/core/hr';

import * as SnackbarEventAction from '@wikus-ui/actions/snackbarEvent';
import * as StockArticleActions from '../../actions/customer-article';
import { CustomerActions as CustomerActionsModel } from '../../model/customer';

import { AdminHomeLayout } from './layout';
import { useActions } from '../../actions';

export const AdminCustomersPage = () => {
  const intl = useIntl();
  const [loading, setLoading] = useState<string>();

  const user = useSelector((state: RootState) => state.user);

  const customerPerPage = 50;
  const customers = useSelector((state: RootState) => state.customers);
  const customersCount = useSelector(
    (state: RootState) => state.customersCount
  );
  const customerActions: typeof CustomerActions = useActions(CustomerActions);
  const snackbarActions: typeof SnackbarEventAction =
    useActions(SnackbarEventAction);

  const customerArticleActions: typeof StockArticleActions = useActions(
    StockArticleActions
  );

  const [filters, setFilters] = useState({});
  const [paginateActions, setPaginateActions] = useState<any>(CustomerActions);

  const history = useHistory();

  const { fetchMore, isLoading, reset } = usePaginate<typeof CustomerActions>({
    itemsPerPage: customerPerPage,
    actions: paginateActions,
    filters,
  });

  const handleEditClick = (customer: Customer) => {
    history.push(`/customer/${customer.id}`);
  };

  const onCustomerFilter = (filters: WikusFilterValue) => {
    setFilters(filters);
  };

  const createDummy = async (customerId: string) => {
    setLoading(customerId);
    const { type } = await customerActions.createDummy(customerId);
    setLoading(undefined);

    if (type === CustomerActionsModel.CREATE_DUMMY_SUCCESS) {
      snackbarActions.addSnackbarEvent({
        message: intl.formatMessage({
          id: 'success.generic',
        }),
        severity: 'success',
      });
      await reset();
    } else {
      snackbarActions.addSnackbarEvent({
        message: intl.formatMessage({
          id: 'errors.generic',
        }),
        severity: 'error',
      });
    }
  }

  const clearDummy = async (customerId: string) => {
    setLoading(customerId);
    const { type } = await customerActions.clearDummy(customerId);
    setLoading(undefined);

    if (type === CustomerActionsModel.CLEAR_DUMMY_SUCCESS) {
      snackbarActions.addSnackbarEvent({
        message: intl.formatMessage({
          id: 'success.generic',
        }),
        severity: 'success',
      });
      await reset();
    } else {
      snackbarActions.addSnackbarEvent({
        message: intl.formatMessage({
          id: 'errors.generic',
        }),
        severity: 'error',
      });
    }
  }

  const triggerStockExport = async () => {
    await customerArticleActions.triggerOldStockExport();
  }

  useEffect(() => {
    setPaginateActions(CustomerActions);
  }, []);

  return (
    <AdminHomeLayout>
      <CustomerFilter onChange={onCustomerFilter} />

      {user.canTriggerStockExport ? (
        <WikusSection spacingDirection="up">
          <WikusPanel
            title="Trigger Customer Export (ONLY FOR TESTING!!!!)"
            description="If you dont't know what this is for, don't press the button, please. This will send several emails to WIKUS."
          >
            <WikusButton
              color="primary"
              variant='outlined'
              onClick={triggerStockExport}
            >
              Trigger Old Stock Export
            </WikusButton>
          </WikusPanel>
        </WikusSection>
      ) : null}

      <WikusSection spacingDirection="up">
        <WikusPanel
          title={intl.formatMessage({ id: 'customers.all' })}
        >
          <WikusInfiniteScroll
            skeletonHeight={100}
            skeletonCount={customerPerPage}
            dataLength={customers?.length}
            next={fetchMore}
            hasMore={customersCount > customers?.length}
            isLoading={isLoading}
          >
            {customers?.map((customer, index) => (
              <WikusItem spacingDirection="down" key={customer.id}>
                {index > 0 ? <WikusHr color="grey" /> : null}
                <WikusModelCard
                  elevation={0}
                  avatar={
                    <WikusAvatar size="large">
                      {getInitials(customer.customerName)}
                    </WikusAvatar>
                  }
                  buttons={[
                    ...(customer.canCreateDummy ? [<WikusButton
                      color="primary"
                      variant="outlined"
                      loading={loading === customer.id}
                      onClick={() => createDummy(customer.id)}
                    >
                      {intl.formatMessage({ id: 'createDummy' })}
                    </WikusButton>] : []),
                    ...(customer.isDummy ? [<WikusButton
                      color="primary"
                      variant="outlined"
                      loading={loading === customer.id}
                      onClick={() => clearDummy(customer.id)}
                    >
                      {intl.formatMessage({ id: 'cleanDummy' })}
                    </WikusButton>] : []),
                    <WikusButton
                      color="primary"
                      onClick={() => handleEditClick(customer)}
                    >
                      {intl.formatMessage({ id: 'edit' })}
                    </WikusButton>
                  ]}
                  details={[{
                    label: intl.formatMessage({
                      id: 'customers.details.customerNumber',
                    }),
                    value: customer.customerNumber,
                  }, {
                    label: intl.formatMessage({
                      id: 'customers.details.addressNumber',
                    }),
                    value: customer.addressNumber,
                  }]}
                  title={customer.customerName}
                />
              </WikusItem>
            ))}
          </WikusInfiniteScroll>
        </WikusPanel>
      </WikusSection>
    </AdminHomeLayout>
  );
};
